import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { company, heroStats } from "../mock";

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await signup(form);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to create account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="hero-gradient relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative flex items-center gap-3">
          <img src={company.logo} alt={company.legal} className="h-12 w-12 rounded-xl bg-white object-cover p-1" />
          <div className="leading-tight">
            <div className="text-base font-bold">{company.name}</div>
            <div className="text-xs text-white/60">{company.legal}</div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight">Start your onboarding journey today.</h1>
          <p className="mt-4 text-white/70">Create your applicant account to track documents, training, and certification.</p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            {heroStats.map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs uppercase tracking-wide text-white/50">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/40">© 2026 {company.legal}</div>
      </div>

      <div className="relative flex items-center justify-center bg-background px-6 py-12">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60">
          <h2 className="text-2xl font-bold text-slate-900">Create account</h2>
          <p className="mt-1 text-sm text-slate-500">New applicants start here.</p>

          {error && (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>First name</Label>
                <Input value={form.first_name} onChange={set("first_name")} required />
              </div>
              <div className="space-y-1.5">
                <Label>Last name</Label>
                <Input value={form.last_name} onChange={set("last_name")} required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Work email</Label>
              <Input type="email" value={form.email} onChange={set("email")} required />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" placeholder="At least 8 characters" value={form.password} onChange={set("password")} required />
            </div>
            <Button type="submit" disabled={busy} className="btn-primary-gradient w-full border-0 text-white">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/" className="font-medium text-violet-600 hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
